/**
 * PHASED MIGRATION (compatibility layer)
 *
 * - Explicit Tamil enrollment overrides legacy tenant routing.
 * - Learners without explicit enrollment keep pre-Tamil platform behavior.
 * - A future phase will migrate Malay learners to explicit enrollment records.
 *
 * This is additive override architecture — not replacement routing.
 */

import type { ResolvedLearnerCurriculum } from './learnerCurriculumTypes';
import {
  isLearnerMalayCurriculum,
  isLearnerTamilCurriculum,
} from './learnerCurriculumTypes';

export function isExplicitTamilLearner(
  curriculum: ResolvedLearnerCurriculum | null | undefined,
): boolean {
  return Boolean(
    curriculum?.ok &&
      curriculum.resolution_source === 'explicit_enrollment' &&
      curriculum.instructional_track === 'tamil',
  );
}

export function isExplicitMalayLearner(
  curriculum: ResolvedLearnerCurriculum | null | undefined,
): boolean {
  return Boolean(
    curriculum?.ok &&
      curriculum.resolution_source === 'explicit_enrollment' &&
      curriculum.instructional_track === 'malay',
  );
}

export function isLegacyMalayCurriculumAllocation(
  curriculum: ResolvedLearnerCurriculum | null | undefined,
): boolean {
  return Boolean(
    curriculum?.ok &&
      curriculum.instructional_track === 'malay' &&
      curriculum.resolution_source === 'legacy_malay_poc_allocation',
  );
}

/** True when the learner should use legacy Malay POC UX (Amir path). */
export function isEffectiveMalayLearner(input: {
  curriculum: ResolvedLearnerCurriculum | null | undefined;
  legacyTenantIsMalay: boolean;
}): boolean {
  if (isLearnerTamilCurriculum(input.curriculum)) return false;
  if (isLearnerMalayCurriculum(input.curriculum)) return true;
  if (isLegacyMalayCurriculumAllocation(input.curriculum)) return true;
  /** Mixed tenants: unresolved curriculum must not inherit Malay POC from school UI language. */
  return false;
}

/** True when the learner should use Tamil tutor UX. */
export function isEffectiveTamilLearner(input: {
  curriculum: ResolvedLearnerCurriculum | null | undefined;
  legacyTenantIsMalay: boolean;
}): boolean {
  if (isLearnerTamilCurriculum(input.curriculum)) return true;
  if (isLearnerMalayCurriculum(input.curriculum)) return false;
  if (isLegacyMalayCurriculumAllocation(input.curriculum)) return false;
  /** Tamil-only legacy tenants without enrollment keep pre-enrollment Tamil UX. */
  if (!input.curriculum?.ok) return !input.legacyTenantIsMalay;
  return false;
}

/**
 * Malay-configured tenants hosting Tamil enrollments: defer Malay/Tamil POC nav
 * until curriculum resolves — avoids Communication/Narrative leakage while loading.
 */
export function shouldDeferMixedTenantLearnerPocNav(input: {
  legacyTenantIsMalay: boolean;
  curriculum: ResolvedLearnerCurriculum | null | undefined;
  curriculumLoading: boolean;
}): boolean {
  if (!input.legacyTenantIsMalay) return false;
  if (isLearnerTamilCurriculum(input.curriculum)) return false;
  if (isLearnerMalayCurriculum(input.curriculum)) return false;
  if (isLegacyMalayCurriculumAllocation(input.curriculum)) return false;
  return input.curriculumLoading || !input.curriculum?.ok;
}

/**
 * Malay-configured tenants may host explicit Tamil enrollments.
 * Wait only for the first curriculum resolution — not background refetches.
 */
export function shouldWaitForCurriculumRouting(input: {
  legacyTenantIsMalay: boolean;
  curriculumLoading: boolean;
  curriculum?: ResolvedLearnerCurriculum | null;
}): boolean {
  if (!input.legacyTenantIsMalay) return false;
  if (input.curriculum) return false;
  return input.curriculumLoading;
}
